import type { EventBus } from "../core/EventBus.ts";
import type { ScreenEffects } from "../rendering/ScreenEffects.ts";
import type { FloatingDamageNumbers } from "../rendering/FloatingDamageNumbers.ts";
import type { GameLoop } from "../engine/GameLoop.ts";
import { KILL_FEEL, killShakeAmp } from "../core/feelBudget.ts";

/** The slice of AudioEngine CombatFeel needs — kept minimal so the handler is
 *  decoupled and unit-testable with a spy (mirrors PayloadDetonationFx's
 *  AudioEngineLike). */
export interface CombatFeelAudio {
  playSfx(name: string, worldX?: number, opts?: { rate?: number }): void;
}

export interface CombatFeelDeps {
  eventBus: EventBus;
  screenEffects: ScreenEffects;
  floatingDmg: FloatingDamageNumbers;
  gameLoop: GameLoop;
  audio?: CombatFeelAudio | null;
}

// S195 / BL-279 — the kill tier's budgets live in THE central table
// (`core/feelBudget.ts`; canon docs/design/COMBAT_FEEL_DIRECTION.md). Values
// are the shipped BL-208 constants, unmoved. shake()/hitStopTier() already
// self-gate reduced motion + perf load, so nothing here needs a manual
// MotionSettings check.
/** Soft score tick reuses the existing "economy_tick" cue — a near-silent sine
 *  with built-in detune (anti-fatigue), the audit's named soft-tick model.
 *  Throttled so a kill-storm never becomes a ping-wall. */
const TICK_CUE = "economy_tick";
/** BL-241 — the combo tiers that earn a milestone callout (the live tier ladder
 *  is 1,2,3,4,5,7,10; these are the "hero" rungs). */
const COMBO_MILESTONES = [5, 7, 10] as const;

/**
 * BL-208 (S189) — the kill-juice loop, render/UI side only.
 *
 * Wires the EXISTING kill / score events into extra "feel": an on-board "+N"
 * score pop at the kill site, a small combo-tiered screen-punch, a brief
 * hit-stop freeze on each combo-multiplier escalation, and a soft score tick.
 * It NEVER touches the sim/RNG/balance — it only reads event payloads and
 * drives render-side systems (ScreenEffects / FloatingDamageNumbers / GameLoop)
 * plus audio, so the golden masters stay byte-identical.
 *
 * Ordering: the kill's REAL awarded points arrive on score.changed
 * {source:"kill"}, which ScoreSystem emits synchronously INSIDE the
 * enemy.killed chain. Since this handler is registered last (wired in main.ts
 * after the core handlers), its score.changed listener fires — nested — before
 * its enemy.killed listener for the same kill, so we capture the delta there
 * and spawn it at the enemy.killed world position. No formula duplication.
 */
export function setupCombatFeel(deps: CombatFeelDeps): () => void {
  const { eventBus, screenEffects, floatingDmg, gameLoop, audio } = deps;

  // BL-208 fix — a LIFO STACK of pending kill scores, NOT a single slot. A
  // kill's score.changed{source:"kill"} fires nested-immediately before its own
  // enemy.killed completes; combatHandlers RE-EMITS enemy.killed for RECURSIVE-
  // LOOP shrapnel kills NESTED inside the primary kill's dispatch. A single slot
  // would be overwritten + consumed by the shrapnel, so the PRIMARY kill (the
  // marquee one) would silently lose its "+N" pop and get a stale combo/mult.
  // The emit structure is properly nested, so a stack pairs each enemy.killed
  // with its own score (push on score.changed, pop on enemy.killed).
  interface PendingKill {
    delta: number;
    combo: number;
    mult: number;
  }
  const pending: PendingKill[] = [];
  let lastMultiplier = 1;
  let lastTickAt = 0;
  // S195 law #5 — same-frame (≤50 ms) score-pop fan state.
  let popFan = 0;
  let lastPopAt = 0;
  // S195/BL-281 — render-side BIG-HIT (crit) read: the projectile.hit payload
  // carries dealt damage AND the projectile's base damage; ≥2× base = the
  // sim-side crit multiplier's worth. The killing blow and its enemy.killed
  // share the same Enemy object, so identity + a tight window pairs them.
  let lastBigHitTarget: unknown = null;
  let lastBigHitAt = 0;
  // BL-241 — combo milestone callouts: the highest tier announced THIS chain
  // (so each of ×5/×7/×10 fires at most once per chain; reset when it ends).
  let announcedMilestone = 0;

  const unsubs: (() => void)[] = [];

  unsubs.push(
    eventBus.on("projectile.hit", (d: unknown) => {
      const ev = d as {
        target?: unknown;
        damage?: number;
        projectile?: { damage?: number };
      };
      const base = ev.projectile?.damage;
      if (
        base !== undefined &&
        base > 0 &&
        (ev.damage ?? 0) >= base * 2 &&
        ev.target
      ) {
        lastBigHitTarget = ev.target;
        lastBigHitAt = Date.now();
      }
    }),
  );

  unsubs.push(
    eventBus.on("score.changed", (d: unknown) => {
      const ev = d as {
        delta?: number;
        source?: string;
        combo?: number;
        multiplier?: number;
      };
      if (ev.source !== "kill") return;
      pending.push({
        delta: ev.delta ?? 0,
        combo: ev.combo ?? 0,
        mult: ev.multiplier ?? 1,
      });
    }),
  );

  unsubs.push(
    eventBus.on("enemy.killed", (d: unknown) => {
      const ev = d as { enemy: { x: number; y: number } };
      const { x, y } = ev.enemy;
      // LIFO: the matching score is the stack top (its score.changed fired
      // nested-immediately before this enemy.killed). Empty only when the kill
      // did not score (e.g. score frozen post-win) — then there is no pop.
      const k = pending.pop();
      const delta = k?.delta ?? 0;
      const combo = k?.combo ?? 0;
      const mult = k?.mult ?? 1;

      // (1) On-board "+N" score pop at the kill site — the headline juice that
      //     makes the 95% of the game (shooting) finally read as consequential.
      //     S195 anti-fight law #5 (BL-280): the pop spawns a fixed step ABOVE
      //     the −N damage number (both used to anchor at y−10 and piled up on
      //     every scoring kill), and same-frame multi-kill pops fan upward so
      //     an AoE burst reads as N rewards, not one smudge.
      if (delta > 0) {
        const now = Date.now();
        if (now - lastPopAt > 50) popFan = 0;
        lastPopAt = now;
        const fan =
          Math.min(popFan++, KILL_FEEL.scorePopFanMax) *
          KILL_FEEL.scorePopFanStep;
        floatingDmg.spawnScore(
          x,
          y - 10 - KILL_FEEL.scorePopLift - fan,
          delta,
        );
      }

      // (2) Small combo-tiered screen-punch (micro normal → a touch bigger as
      //     the chain grows). shake() damps to 0 under reduced motion/perf load.
      // S195/BL-281 — a crit-KILL punches a step harder than a plain kill
      // (still under the tier cap, so it never reaches the boss/arrival band).
      const isCritKill =
        lastBigHitTarget !== null &&
        lastBigHitTarget === (ev as { enemy?: unknown }).enemy &&
        Date.now() - lastBigHitAt < 100;
      if (isCritKill) lastBigHitTarget = null;
      const amp = Math.min(
        killShakeAmp(mult) + (isCritKill ? 0.4 : 0),
        KILL_FEEL.shakeMax,
      );
      screenEffects.shake(amp, KILL_FEEL.shakeMs);

      // (3) A brief "short" hit-stop the moment the multiplier ESCALATES — once
      //     per new combo tier, not per kill. A fresh chain (combo<=1) re-arms
      //     the ladder. hitStopTier() self-guards reduced motion and the
      //     GameLoop min-gap policy throttles back-to-back freezes.
      if (combo <= 1) {
        lastMultiplier = 1;
        announcedMilestone = 0; // a fresh chain re-arms every milestone
      }
      if (mult > lastMultiplier) gameLoop.hitStopTier("short");
      lastMultiplier = mult;

      // (3b) BL-241 — combo MILESTONE callout: the first time a chain reaches a
      //      milestone tier (×5/×7/×10), emit `combo.milestone` so the
      //      presentation layer (main.ts) can fire the banner + flourish + flash
      //      + sting. Once per tier per chain; the highest newly-crossed tier in
      //      a single kill wins (no double-banner on a big jump).
      let tier = 0;
      for (const ms of COMBO_MILESTONES) {
        if (mult >= ms && ms > announcedMilestone) tier = ms;
      }
      if (tier > 0) {
        announcedMilestone = tier;
        eventBus.emit("combo.milestone", { mult: tier });
      }

      // (4) Soft, throttled score tick (reused cue, panned to the kill
      //     column). S195/BL-281: the tick now climbs a semitone per combo-
      //     multiplier step (kill-storms audibly ESCALATE instead of pinging
      //     flat — the audit's kill≈hit flatness), and a crit-kill's tick
      //     rides a bright +7 on top of the ladder.
      const now = Date.now();
      if (audio && now - lastTickAt >= KILL_FEEL.tickThrottleMs) {
        lastTickAt = now;
        const semis = Math.min(mult - 1, 12) + (isCritKill ? 7 : 0);
        audio.playSfx(TICK_CUE, x, { rate: Math.pow(2, semis / 12) });
      }
    }),
  );

  // Reset the combo-escalation ladder when a chain times out, so the next
  // chain's tier-ups re-trigger the freeze.
  unsubs.push(
    eventBus.on("score.comboEnd", () => {
      lastMultiplier = 1;
      announcedMilestone = 0; // BL-241 — re-arm milestones for the next chain
    }),
  );

  return () => {
    for (const unsub of unsubs) unsub();
  };
}
